import { useCallback } from 'react';
import { BirthInfo } from '../contexts/BirthInfoContext';
import {
  FourPillars,
  ElementBalance,
  TenGodAnalysis,
  analyzeElements,
} from '../utils/fourPillars';

interface PdfData {
  birthInfo: BirthInfo;
  fourPillars: FourPillars;
  elementBalance: ElementBalance;
  tenGods: TenGodAnalysis;
  zodiacSign?: string;
}

function scoreBar(score: number, max = 8): string {
  const filled = Math.round((score / max) * 10);
  return '■'.repeat(filled) + '□'.repeat(10 - filled);
}

function buildPdfHtml(data: PdfData): string {
  const { birthInfo, fourPillars, elementBalance, tenGods, zodiacSign } = data;
  const birthDateObj = new Date(birthInfo.birthDate);
  const birthYear = birthDateObj.getFullYear();
  const birthMonth = birthDateObj.getMonth() + 1;
  const birthDay = birthDateObj.getDate();
  const currentYear = new Date().getFullYear();
  const age = currentYear - birthYear;
  const genderLabel = birthInfo.gender === 'male' ? '남성' : '여성';
  const ea = analyzeElements(elementBalance);

  const elementRows = [
    ['목(木)', elementBalance.wood],
    ['화(火)', elementBalance.fire],
    ['토(土)', elementBalance.earth],
    ['금(金)', elementBalance.metal],
    ['수(水)', elementBalance.water],
  ]
    .map(([k, v]) => `<tr><td>${k}</td><td>${scoreBar(v as number)}</td><td style="text-align:right">${v}</td></tr>`)
    .join('');

  const tenGodRows = tenGods.entries
    .map(
      (e) =>
        `<tr><td>${e.pillar}</td><td style="text-align:center">${e.stem}</td><td style="text-align:center">${e.stemGod}</td><td style="text-align:center">${e.branch}</td><td style="text-align:center">${e.branchGod}</td></tr>`,
    )
    .join('');

  const dominantGodsRows = tenGods.dominantGods
    .map((g) => `<tr><td>${g.god}</td><td style="text-align:center">${g.count}회</td><td>${g.shortMeaning}</td></tr>`)
    .join('');

  return `<!DOCTYPE html>
<html lang="ko">
<head>
<meta charset="UTF-8" />
<title>명 DESTINY GPT · 사주팔자 분석 리포트</title>
<link href="https://fonts.googleapis.com/css2?family=Noto+Serif+KR:wght@400;600;700&display=swap" rel="stylesheet" />
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'Noto Serif KR', serif;
    background: #fff;
    color: #1a1a2e;
    padding: 40px 48px;
    max-width: 800px;
    margin: 0 auto;
    font-size: 13px;
    line-height: 1.7;
  }
  .header {
    text-align: center;
    border-bottom: 2px solid #c9a227;
    padding-bottom: 24px;
    margin-bottom: 28px;
  }
  .logo-badge {
    display: inline-block;
    width: 52px; height: 52px;
    background: #1a0f2e;
    border: 2px solid #c9a227;
    border-radius: 10px;
    line-height: 52px;
    font-size: 26px;
    color: #c9a227;
    margin-bottom: 8px;
  }
  .app-title { font-size: 22px; font-weight: 700; color: #1a1a2e; letter-spacing: 0.1em; }
  .app-sub { font-size: 10px; color: #888; letter-spacing: 0.3em; text-transform: uppercase; margin-top: 2px; }
  .report-title { font-size: 14px; color: #c9a227; font-weight: 600; margin-top: 10px; }
  
  .profile-card {
    background: #f9f7f1;
    border: 1px solid #c9a22740;
    border-radius: 12px;
    padding: 20px 24px;
    margin-bottom: 28px;
    display: flex;
    align-items: center;
    gap: 20px;
  }
  .profile-avatar {
    width: 60px; height: 60px;
    background: linear-gradient(135deg, #c9a227, #e8c547);
    border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    font-size: 24px; font-weight: 900; color: #1a0f2e;
    flex-shrink: 0;
  }
  .profile-info h2 { font-size: 18px; font-weight: 700; color: #1a1a2e; }
  .profile-info p { font-size: 12px; color: #666; margin-top: 4px; }
  .badges { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
  .badge {
    padding: 2px 10px;
    border: 1px solid #c9a22760;
    border-radius: 20px;
    font-size: 11px;
    color: #555;
    background: #fff;
  }
  
  h3.section {
    font-size: 15px;
    font-weight: 700;
    color: #1a1a2e;
    border-left: 4px solid #c9a227;
    padding-left: 10px;
    margin: 24px 0 14px;
  }
  
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th {
    background: #1a0f2e;
    color: #c9a227;
    padding: 8px 10px;
    text-align: left;
    font-weight: 600;
  }
  td { padding: 7px 10px; border-bottom: 1px solid #eee; }
  tr:nth-child(even) td { background: #fafafa; }
  
  .pillar-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 24px;
  }
  .pillar-card {
    border: 1px solid #c9a22750;
    border-radius: 10px;
    padding: 12px;
    text-align: center;
    background: #f9f7f1;
  }
  .pillar-label { font-size: 10px; color: #888; margin-bottom: 6px; }
  .pillar-stem { font-size: 28px; font-weight: 900; color: #c9a227; }
  .pillar-branch { font-size: 22px; font-weight: 700; color: #1a1a2e; margin-top: 4px; }
  .pillar-combined { font-size: 11px; color: #666; margin-top: 6px; }
  
  .analysis-box {
    background: #f9f7f1;
    border-left: 4px solid #c9a227;
    border-radius: 0 8px 8px 0;
    padding: 12px 16px;
    margin-bottom: 14px;
    font-size: 12px;
    line-height: 1.8;
    color: #333;
  }
  
  .footer {
    margin-top: 40px;
    padding-top: 20px;
    border-top: 1px solid #eee;
    text-align: center;
    font-size: 10px;
    color: #aaa;
  }
  
  @media print {
    body { padding: 20px 28px; }
    @page { margin: 1cm; size: A4; }
    button { display: none !important; }
  }
</style>
</head>
<body>
  <!-- 프린트 버튼 (화면에서만 보임) -->
  <div style="text-align:center;margin-bottom:20px;print:none">
    <button onclick="window.print()"
      style="padding:10px 28px;background:linear-gradient(135deg,#c9a227,#e8c547);border:none;border-radius:24px;font-size:14px;font-weight:700;color:#1a0f2e;cursor:pointer;font-family:inherit">
      🖨 PDF로 저장 / 인쇄
    </button>
  </div>

  <!-- 헤더 -->
  <div class="header">
    <div class="logo-badge">명</div>
    <div class="app-title">DESTINY GPT</div>
    <div class="app-sub">운명 분석 AI</div>
    <div class="report-title">사주팔자 종합 분석 리포트</div>
  </div>

  <!-- 프로필 -->
  <div class="profile-card">
    <div class="profile-avatar">${birthInfo.name ? birthInfo.name.charAt(0) : '명'}</div>
    <div class="profile-info">
      <h2>${birthInfo.name || '운명의 주인공'} <span style="font-size:13px;color:#888;font-weight:400">${age}세 · ${genderLabel}</span></h2>
      <p>${birthInfo.birthPlace} 출생</p>
      <div class="badges">
        <span class="badge">📅 ${birthYear}년 ${birthMonth}월 ${birthDay}일</span>
        <span class="badge">⏰ ${birthInfo.birthTime}</span>
        ${zodiacSign ? `<span class="badge">✨ ${zodiacSign}</span>` : ''}
        <span class="badge">일간 ${fourPillars.day.stem}(日干)</span>
        <span class="badge">생성일 ${new Date().getFullYear()}.${new Date().getMonth() + 1}.${new Date().getDate()}</span>
      </div>
    </div>
  </div>

  <!-- 사주팔자 -->
  <h3 class="section">사주팔자 (四柱八字)</h3>
  <div class="pillar-grid">
    ${['년주(年)', '월주(月)', '일주(日)', '시주(時)'].map((label, i) => {
      const p = [fourPillars.year, fourPillars.month, fourPillars.day, fourPillars.hour][i];
      return `<div class="pillar-card">
        <div class="pillar-label">${label}</div>
        <div class="pillar-stem">${p.stem}</div>
        <div class="pillar-branch">${p.branch}</div>
        <div class="pillar-combined">${p.combined}</div>
      </div>`;
    }).join('')}
  </div>

  <!-- 십신 분석 -->
  <h3 class="section">십신(十神) 분석 — 일간 ${tenGods.dayStem} 기준</h3>
  <table>
    <thead><tr><th>기둥</th><th style="text-align:center">천간</th><th style="text-align:center">십신</th><th style="text-align:center">지지</th><th style="text-align:center">지장간십신</th></tr></thead>
    <tbody>${tenGodRows}</tbody>
  </table>

  <h3 class="section" style="margin-top:20px">주요 십신 특성</h3>
  <table>
    <thead><tr><th>십신</th><th style="text-align:center">빈도</th><th>의미</th></tr></thead>
    <tbody>${dominantGodsRows}</tbody>
  </table>

  <!-- 오행 분석 -->
  <h3 class="section">오행(五行) 분석</h3>
  <table>
    <thead><tr><th>오행</th><th>강도</th><th style="text-align:right">점수</th></tr></thead>
    <tbody>${elementRows}</tbody>
  </table>
  <div class="analysis-box" style="margin-top:12px">
    <strong>가장 강한 오행:</strong> ${ea.strongest} &nbsp;|&nbsp; <strong>가장 약한 오행:</strong> ${ea.weakest}<br/>
    ${ea.description}
  </div>

  <!-- 종합 해석 -->
  <h3 class="section">종합 해석</h3>
  <div class="analysis-box">
    일주가 <strong>${fourPillars.day.combined}</strong>인 당신은
    ${(fourPillars.day.stem === '갑' || fourPillars.day.stem === '을') ? '목 기운의 영향으로 성장과 발전의 에너지를 가지고 있습니다.' : ''}
    ${(fourPillars.day.stem === '병' || fourPillars.day.stem === '정') ? '화 기운의 영향으로 열정과 창의성이 뛰어납니다.' : ''}
    ${(fourPillars.day.stem === '무' || fourPillars.day.stem === '기') ? '토 기운의 영향으로 안정적이고 신뢰감 있는 성품을 지녔습니다.' : ''}
    ${(fourPillars.day.stem === '경' || fourPillars.day.stem === '신') ? '금 기운의 영향으로 결단력과 추진력이 강합니다.' : ''}
    ${(fourPillars.day.stem === '임' || fourPillars.day.stem === '계') ? '수 기운의 영향으로 지혜롭고 유연한 사고를 가지고 있습니다.' : ''}
  </div>
  <div class="analysis-box">
    년주 <strong>${fourPillars.year.combined}</strong>는 조상과 부모로부터 받은 기운을 나타내며,
    월주 <strong>${fourPillars.month.combined}</strong>는 형제 관계와 직업운을 보여줍니다.
    시주 <strong>${fourPillars.hour.combined}</strong>는 자녀운과 말년운을 의미합니다.
  </div>

  <!-- 푸터 -->
  <div class="footer">
    <p>이 리포트는 命 DESTINY GPT (AI 운세 분석 서비스)에 의해 자동 생성되었습니다.</p>
    <p style="margin-top:4px">사주 분석은 참고용이며, 중요한 결정은 반드시 전문가와 상담하시기 바랍니다.</p>
  </div>
</body>
</html>`;
}

// ─── Annual Report PDF ────────────────────────────────────────────────
interface AnnualPdfData {
  birthInfo: BirthInfo;
  year: number;
  dayStem: string;
  yearLabel: string;
  yearOverview: string;
  compatScore: number;
  compatMsg: string;
  luckyItems: { color: string; direction: string; number: string; gem: string };
  months: {
    month: number;
    label: string;
    overall: number;
    wealth: number;
    love: number;
    career: number;
    health: number;
    relation: number;
    keyword: string;
    advice: string;
  }[];
}

function buildAnnualPdfHtml(data: AnnualPdfData): string {
  const { birthInfo, year, dayStem, yearLabel, yearOverview, compatScore, compatMsg, luckyItems, months } = data;
  const birthYear = new Date(birthInfo.birthDate).getFullYear();
  const age = year - birthYear;
  const stars = '★'.repeat(compatScore) + '☆'.repeat(5 - compatScore);
  const scoreColor = compatScore >= 4 ? '#d97706' : compatScore >= 3 ? '#7c3aed' : '#ea580c';

  const monthRows = months.map((m) => {
    const overall = m.overall;
    const bar = '■'.repeat(overall) + '□'.repeat(5 - overall);
    return `<tr>
      <td>${m.label}</td>
      <td style="text-align:center;color:${overall >= 4 ? '#d97706' : overall >= 3 ? '#6d28d9' : '#ea580c'}">${bar} ${overall}/5</td>
      <td>${m.wealth}/5</td><td>${m.love}/5</td><td>${m.career}/5</td><td>${m.health}/5</td>
      <td style="color:#555">${m.keyword}</td>
    </tr>`;
  }).join('');

  const bestMonth = [...months].sort((a, b) => b.overall - a.overall)[0];
  const worstMonth = [...months].sort((a, b) => a.overall - b.overall)[0];

  return `<!DOCTYPE html>
<html lang="ko">
<head>
<meta charset="UTF-8"/>
<title>명 DESTINY GPT · ${year}년 1년 운세 리포트</title>
<link href="https://fonts.googleapis.com/css2?family=Noto+Serif+KR:wght@400;600;700&display=swap" rel="stylesheet"/>
<style>
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'Noto Serif KR',serif; background:#fff; color:#1a1a2e; padding:40px 48px; max-width:800px; margin:0 auto; font-size:13px; line-height:1.7; }
  .header { text-align:center; border-bottom:2px solid #c9a227; padding-bottom:24px; margin-bottom:28px; }
  .logo-badge { display:inline-block; width:52px; height:52px; background:#1a0f2e; border:2px solid #c9a227; border-radius:10px; line-height:52px; font-size:26px; color:#c9a227; margin-bottom:8px; }
  .app-title { font-size:22px; font-weight:700; color:#1a1a2e; letter-spacing:0.1em; }
  .app-sub { font-size:10px; color:#888; letter-spacing:0.3em; text-transform:uppercase; margin-top:2px; }
  .report-title { font-size:14px; color:#c9a227; font-weight:600; margin-top:10px; }
  .profile-card { background:#f9f7f1; border:1px solid #c9a22740; border-radius:12px; padding:20px 24px; margin-bottom:28px; }
  .badge { display:inline-block; padding:2px 10px; border:1px solid #c9a22760; border-radius:20px; font-size:11px; color:#555; background:#fff; margin:3px 2px; }
  h3.section { font-size:15px; font-weight:700; color:#1a1a2e; border-left:4px solid #c9a227; padding-left:10px; margin:24px 0 14px; }
  table { width:100%; border-collapse:collapse; font-size:12px; }
  th { background:#1a0f2e; color:#c9a227; padding:8px 10px; text-align:left; font-weight:600; }
  td { padding:7px 10px; border-bottom:1px solid #eee; }
  tr:nth-child(even) td { background:#fafafa; }
  .compat-box { background:#fdf8ec; border:1px solid #c9a22750; border-radius:12px; padding:16px 20px; margin-bottom:20px; }
  .lucky-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:20px; }
  .lucky-item { text-align:center; background:#f9f7f1; border:1px solid #c9a22740; border-radius:10px; padding:12px 8px; }
  .lucky-label { font-size:10px; color:#888; margin-bottom:4px; }
  .lucky-value { font-size:13px; font-weight:700; color:#1a1a2e; }
  .highlight-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:20px; }
  .highlight-card { border-radius:10px; padding:14px 16px; }
  .footer { margin-top:40px; padding-top:20px; border-top:1px solid #eee; text-align:center; font-size:10px; color:#aaa; }
  @media print { body{padding:20px 28px;} @page{margin:1cm;size:A4;} button{display:none!important;} }
</style>
</head>
<body>
  <div style="text-align:center;margin-bottom:20px">
    <button onclick="window.print()" style="padding:10px 28px;background:linear-gradient(135deg,#c9a227,#e8c547);border:none;border-radius:24px;font-size:14px;font-weight:700;color:#1a0f2e;cursor:pointer;font-family:inherit">🖨 PDF로 저장 / 인쇄</button>
  </div>
  <div class="header">
    <div class="logo-badge">명</div>
    <div class="app-title">DESTINY GPT</div>
    <div class="app-sub">운명 분석 AI</div>
    <div class="report-title">${year}년 1년 운세 리포트</div>
  </div>
  <div class="profile-card">
    <h2 style="font-size:18px;font-weight:700;margin-bottom:8px">${birthInfo.name || '운명의 주인공'} <span style="font-size:13px;color:#888;font-weight:400">${age}세 · ${birthInfo.birthPlace} 출생</span></h2>
    <div>
      <span class="badge">📅 ${birthInfo.birthDate}</span>
      <span class="badge">일간 ${dayStem}(日干)</span>
      <span class="badge">⏰ ${birthInfo.birthTime}</span>
      <span class="badge">생성일 ${new Date().getFullYear()}.${new Date().getMonth()+1}.${new Date().getDate()}</span>
    </div>
  </div>
  <h3 class="section">${year}년 — ${yearLabel}</h3>
  <p style="color:#555;font-size:12px;margin-bottom:16px;line-height:1.8">${yearOverview}</p>
  <div class="compat-box">
    <div style="display:flex;align-items:center;gap:16px">
      <div>
        <p style="font-size:11px;color:#888;margin-bottom:4px">년운 궁합 점수</p>
        <p style="font-size:22px;font-weight:900;color:${scoreColor}">${stars}</p>
        <p style="font-size:11px;font-weight:700;color:${scoreColor}">${compatScore}/5</p>
      </div>
      <div>
        <p style="font-size:13px;font-weight:700;color:#1a1a2e;margin-bottom:4px">${compatMsg}</p>
      </div>
    </div>
  </div>
  <h3 class="section">행운 아이템</h3>
  <div class="lucky-grid">
    <div class="lucky-item"><div class="lucky-label">행운 컬러</div><div class="lucky-value">${luckyItems.color}</div></div>
    <div class="lucky-item"><div class="lucky-label">길한 방향</div><div class="lucky-value">${luckyItems.direction}</div></div>
    <div class="lucky-item"><div class="lucky-label">행운 숫자</div><div class="lucky-value">${luckyItems.number}</div></div>
    <div class="lucky-item"><div class="lucky-label">행운 보석</div><div class="lucky-value">${luckyItems.gem}</div></div>
  </div>
  <h3 class="section">월별 하이라이트</h3>
  <div class="highlight-grid">
    <div class="highlight-card" style="background:#fdf8ec;border:1px solid #c9a22750">
      <p style="font-size:11px;color:#888;margin-bottom:4px">☀️ 최고 운세의 달</p>
      <p style="font-size:18px;font-weight:900;color:#c9a227">${bestMonth.label}</p>
      <p style="font-size:11px;color:#666;margin-top:4px">${bestMonth.keyword} · 전체 ${bestMonth.overall}/5</p>
      <p style="font-size:11px;color:#777;margin-top:6px;line-height:1.6">${bestMonth.advice}</p>
    </div>
    <div class="highlight-card" style="background:#f5f5f5;border:1px solid #ddd">
      <p style="font-size:11px;color:#888;margin-bottom:4px">🛡 주의가 필요한 달</p>
      <p style="font-size:18px;font-weight:900;color:#666">${worstMonth.label}</p>
      <p style="font-size:11px;color:#666;margin-top:4px">${worstMonth.keyword} · 전체 ${worstMonth.overall}/5</p>
    </div>
  </div>
  <h3 class="section">월별 운세 상세</h3>
  <table>
    <thead><tr><th>월</th><th>종합</th><th>재물</th><th>애정</th><th>직업</th><th>건강</th><th>키워드</th></tr></thead>
    <tbody>${monthRows}</tbody>
  </table>
  <div class="footer">
    <p>이 리포트는 命 DESTINY GPT (AI 운세 분석 서비스)에 의해 자동 생성되었습니다.</p>
    <p style="margin-top:4px">사주 분석은 참고용이며, 중요한 결정은 반드시 전문가와 상담하시기 바랍니다.</p>
  </div>
</body>
</html>`;
}

// ─── Lifetime Report PDF ────────────────────────────────────────────────
interface DecadeEntry { stem: string; branch: string; score: number; }
interface LifePillarEntry { label: string; peakAges: string; score: number; }

interface LifetimePdfData {
  birthInfo: BirthInfo;
  dayStem: string;
  dayBranch: string;
  strongestElement: string;
  weakestElement: string;
  lifeMessage: string;
  decades: DecadeEntry[];
  lifePillars: LifePillarEntry[];
  strengths: string[];
  cautions: string[];
  dominantGods: { god: string; shortMeaning: string }[];
}

function buildLifetimePdfHtml(data: LifetimePdfData): string {
  const { birthInfo, dayStem, dayBranch, strongestElement, weakestElement, lifeMessage, decades, lifePillars, strengths, cautions, dominantGods } = data;
  const birthYear = new Date(birthInfo.birthDate).getFullYear();
  const currentYear = new Date().getFullYear();
  const age = currentYear - birthYear;
  const currentDecadeIdx = Math.floor(age / 10);

  const decadeRows = decades.map((d, idx) => {
    const startAge = idx * 10;
    const startYear = birthYear + startAge;
    const isCurrent = idx === currentDecadeIdx;
    const scoreLabel = d.score >= 4 ? '길(吉)' : d.score >= 3 ? '평(平)' : '주의';
    const scoreColor = d.score >= 4 ? '#d97706' : d.score >= 3 ? '#6d28d9' : '#ea580c';
    return `<tr${isCurrent ? ' style="background:#fdf8ec"' : ''}>
      <td${isCurrent ? ' style="font-weight:700;color:#c9a227"' : ''}>${d.stem}${d.branch} ${isCurrent ? '← 현재' : ''}</td>
      <td>${startYear}~${startYear+9}년</td>
      <td>${startAge}~${startAge+9}세</td>
      <td style="color:${scoreColor};font-weight:600">${'★'.repeat(d.score)}${'☆'.repeat(5-d.score)} ${scoreLabel}</td>
    </tr>`;
  }).join('');

  const pillarRows = lifePillars.map((p) => `<tr>
    <td>${p.label}</td><td style="text-align:center;font-weight:700;color:#c9a227">${p.peakAges}</td>
    <td style="color:${p.score >= 4 ? '#d97706' : '#6d28d9'}">${'★'.repeat(p.score)}${'☆'.repeat(5-p.score)}</td>
  </tr>`).join('');

  const strengthsList = strengths.map((s) => `<li style="margin-bottom:6px">${s}</li>`).join('');
  const cautionsList = cautions.map((c) => `<li style="margin-bottom:6px">${c}</li>`).join('');
  const godsList = dominantGods.slice(0, 4).map((g) => `<li style="margin-bottom:6px"><strong>${g.god}</strong> — ${g.shortMeaning}</li>`).join('');

  return `<!DOCTYPE html>
<html lang="ko">
<head>
<meta charset="UTF-8"/>
<title>명 DESTINY GPT · 평생 운세 리포트</title>
<link href="https://fonts.googleapis.com/css2?family=Noto+Serif+KR:wght@400;600;700&display=swap" rel="stylesheet"/>
<style>
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'Noto Serif KR',serif; background:#fff; color:#1a1a2e; padding:40px 48px; max-width:800px; margin:0 auto; font-size:13px; line-height:1.7; }
  .header { text-align:center; border-bottom:2px solid #c9a227; padding-bottom:24px; margin-bottom:28px; }
  .logo-badge { display:inline-block; width:52px; height:52px; background:#1a0f2e; border:2px solid #c9a227; border-radius:10px; line-height:52px; font-size:26px; color:#c9a227; margin-bottom:8px; }
  .app-title { font-size:22px; font-weight:700; color:#1a1a2e; letter-spacing:0.1em; }
  .app-sub { font-size:10px; color:#888; letter-spacing:0.3em; text-transform:uppercase; margin-top:2px; }
  .report-title { font-size:14px; color:#c9a227; font-weight:600; margin-top:10px; }
  .profile-card { background:#f9f7f1; border:1px solid #c9a22740; border-radius:12px; padding:20px 24px; margin-bottom:28px; }
  .badge { display:inline-block; padding:2px 10px; border:1px solid #c9a22760; border-radius:20px; font-size:11px; color:#555; background:#fff; margin:3px 2px; }
  h3.section { font-size:15px; font-weight:700; color:#1a1a2e; border-left:4px solid #c9a227; padding-left:10px; margin:24px 0 14px; }
  table { width:100%; border-collapse:collapse; font-size:12px; }
  th { background:#1a0f2e; color:#c9a227; padding:8px 10px; text-align:left; font-weight:600; }
  td { padding:7px 10px; border-bottom:1px solid #eee; }
  tr:nth-child(even) td { background:#fafafa; }
  .message-box { background:#fdf8ec; border-left:4px solid #c9a227; padding:16px 20px; border-radius:0 10px 10px 0; margin-bottom:20px; font-size:13px; line-height:1.9; color:#333; }
  .two-col { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:20px; }
  .col-card { border-radius:10px; padding:16px; }
  .footer { margin-top:40px; padding-top:20px; border-top:1px solid #eee; text-align:center; font-size:10px; color:#aaa; }
  @media print { body{padding:20px 28px;} @page{margin:1cm;size:A4;} button{display:none!important;} }
</style>
</head>
<body>
  <div style="text-align:center;margin-bottom:20px">
    <button onclick="window.print()" style="padding:10px 28px;background:linear-gradient(135deg,#c9a227,#e8c547);border:none;border-radius:24px;font-size:14px;font-weight:700;color:#1a0f2e;cursor:pointer;font-family:inherit">🖨 PDF로 저장 / 인쇄</button>
  </div>
  <div class="header">
    <div class="logo-badge">명</div>
    <div class="app-title">DESTINY GPT</div>
    <div class="app-sub">운명 분석 AI</div>
    <div class="report-title">평생 운세 리포트 — 운명 지도</div>
  </div>
  <div class="profile-card">
    <h2 style="font-size:18px;font-weight:700;margin-bottom:8px">${birthInfo.name || '운명의 주인공'} <span style="font-size:13px;color:#888;font-weight:400">${age}세 · ${birthInfo.birthPlace} 출생</span></h2>
    <div>
      <span class="badge">📅 ${birthInfo.birthDate}</span>
      <span class="badge">주 오행 ${strongestElement}</span>
      <span class="badge">생성일 ${new Date().getFullYear()}.${new Date().getMonth()+1}.${new Date().getDate()}</span>
    </div>
  </div>
  <h3 class="section">${dayStem}${dayBranch} 일주 — 삶의 메시지</h3>
  <div class="message-box">${lifeMessage}</div>
  <h3 class="section">주요 십신(十神) 특성</h3>
  <ul style="padding-left:20px;margin-bottom:20px;font-size:12px;line-height:2">${godsList}</ul>
  <h3 class="section">10년 대운(大운) 흐름</h3>
  <table>
    <thead><tr><th>대운</th><th>기간</th><th>나이</th><th>운세</th></tr></thead>
    <tbody>${decadeRows}</tbody>
  </table>
  <h3 class="section">인생 5대 영역 정점 시기</h3>
  <table>
    <thead><tr><th>영역</th><th style="text-align:center">정점 나이</th><th>강도</th></tr></thead>
    <tbody>${pillarRows}</tbody>
  </table>
  <h3 class="section">평생 강점 &amp; 주의 사항</h3>
  <div class="two-col">
    <div class="col-card" style="background:#f0fdf4;border:1px solid #86efac50">
      <p style="font-size:12px;font-weight:700;color:#166534;margin-bottom:10px">✅ 평생 핵심 강점</p>
      <ul style="padding-left:16px;font-size:12px;line-height:1.8;color:#166534">${strengthsList}</ul>
      <li style="list-style:none;margin-top:6px;font-size:12px;color:#166534">오행 ${strongestElement} 기운이 강함 — 이를 살린 직업·활동 추천</li>
    </div>
    <div class="col-card" style="background:#fff7ed;border:1px solid #fed7aa50">
      <p style="font-size:12px;font-weight:700;color:#9a3412;margin-bottom:10px">⚠️ 평생 주의 사항</p>
      <ul style="padding-left:16px;font-size:12px;line-height:1.8;color:#9a3412">${cautionsList}</ul>
      <li style="list-style:none;margin-top:6px;font-size:12px;color:#9a3412">오행 ${weakestElement} 보완 — 관련 색상·방향으로 균형 유지</li>
    </div>
  </div>
  <div class="footer">
    <p>이 리포트는 命 DESTINY GPT (AI 운세 분석 서비스)에 의해 자동 생성되었습니다.</p>
    <p style="margin-top:4px">사주 분석은 참고용이며, 중요한 결정은 반드시 전문가와 상담하시기 바랍니다.</p>
  </div>
</body>
</html>`;
}

export function usePdfDownload() {
  const downloadPdf = useCallback((data: PdfData) => {
    const html = buildPdfHtml(data);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (win) {
      win.onload = () => { setTimeout(() => { win.print(); }, 500); };
    }
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  }, []);

  const downloadAnnualPdf = useCallback((data: AnnualPdfData) => {
    const html = buildAnnualPdfHtml(data);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (win) {
      win.onload = () => { setTimeout(() => { win.print(); }, 500); };
    }
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  }, []);

  const downloadLifetimePdf = useCallback((data: LifetimePdfData) => {
    const html = buildLifetimePdfHtml(data);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (win) {
      win.onload = () => { setTimeout(() => { win.print(); }, 500); };
    }
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  }, []);

  return { downloadPdf, downloadAnnualPdf, downloadLifetimePdf };
}
